import { useDispatch } from 'react-redux'

import type { ModifiedChargeSheetLineItemInput } from '~/api/graphql/generated/types'
import { getIsRetailOrderLineItem } from '~/components/dashboard/invoices/invoiceUtils'
import { editChargeSheetItem } from '~/store/duck/clientFinanceData'
import type { InvoiceLineItem } from '~/types'

/**
 * Hook for applying discounts to charge sheet items
 * Supports both dollar amount and percentage discounts (mutually exclusive)
 * Only applies to InvoiceLineItem (retail items don't support discounts)
 *
 * Note: discountPercentage is already in decimal format (0.15 for 15%)
 * The ApplyManualDiscountModal handles conversion from percentage display (15%) to decimal (0.15)
 *
 * @returns Handler function for applying discounts
 */
export const useChargeItemDiscount = () => {
  const dispatch = useDispatch()

  return (
    item: InvoiceLineItem,
    discountAmount: number,
    quantity: number,
    discountPercentage?: number,
  ) => {
    const isRetailOrder = getIsRetailOrderLineItem(item)

    if (!isRetailOrder) {
      // For invoice line items, use the charge sheet item edit with discount and quantity
      const updateInput: ModifiedChargeSheetLineItemInput = {
        id: item.id,
        expectedModification: item.modificationDate,
        expectedSoapLogModification: item.soapLogModificationDate,
        quantity,
      }

      // Only add the discount field that's being used (mutually exclusive)
      if (discountPercentage !== undefined) {
        // Percentage mode - only include discountPerc
        updateInput.discountPerc = discountPercentage
      } else {
        // Price mode - only include discountAmount
        updateInput.discountAmount = discountAmount
      }

      dispatch(
        editChargeSheetItem({
          updateItemInput: [updateInput],
        }),
      )
    }
    // Note: Retail order line items don't support discountAmount in their update input
    // Discounts for retail items are typically handled through different mechanisms
  }
}
